import { useMemo, useState } from 'react'
import { MenuItem, Typography } from '@mui/material'
import useGetEntrataVendors from '@/hooks/useGetEntrataVendors'
import { useGetOrganization } from '@/service-library/hooks/organizations'
import { EntrataVendor } from '@/types/entrata-vendor'
import { LiveListEntity } from '@/types/live-lists'
import { decodeHtml } from '@/utils/text'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectGridFieldsContext } from '../ProjectGridFieldsProvider'
import useFieldValue from '../useFieldValue'
import BasePickerField from './BasePickerField'
import { liveListNoneOption } from './LiveListPickerField'

type EntrataLiveVendorListProps = {
  jsonValue: LiveListEntity<EntrataVendor> | null
  onManualChange: (
    newValue: string | null,
    refId: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jsonObject?: Record<string, any> | null,
  ) => void
  disabled?: boolean
  openOnFocus?: boolean
}

function getNameToDisplay(vendor?: EntrataVendor) {
  if (!vendor) return ''
  return decodeHtml(vendor.display_name || vendor.vendor_name) || ''
}

export default function EntrataLiveVendorList({
  jsonValue,
  onManualChange,
  openOnFocus = false,
  disabled,
}: EntrataLiveVendorListProps) {
  const { document, isDisabled } = useDocumentContext()

  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(
    jsonValue?.entity_id === 'None'
      ? 'None'
      : getNameToDisplay(jsonValue?.entity),
  )
  const [inputValue, setInputValue] = useState(
    jsonValue?.entity_id === 'None'
      ? 'None'
      : getNameToDisplay(jsonValue?.entity),
  )

  const { organization, isFetched } = useGetOrganization({
    id: document?.owner_org_id || '',
    filters: {
      fields__include: 'org_type',
    },
    enabled: !!document,
  })
  const isPropertyOrg = organization?.org_type?.code === 'entrata_property'
  const clientOrgId = isPropertyOrg ? organization.parent_org_id || '' : ''
  const { entrataVendors, hasNextPage, fetchNextPage, isPending, isFetching } =
    useGetEntrataVendors({
      filters: {
        client_org_id: clientOrgId,
        vendor_name: searchValue,
        property_ids: organization?.code || '',
      },
      paginator: 'page',
      enabled: open && !!clientOrgId && !!searchValue,
    })

  const needToTypeFirst = open && !!clientOrgId && !searchValue

  const options = useMemo(() => {
    if (isPending) return []
    const entrataOptions = entrataVendors.map((vendor) => ({
      entity_id: `${vendor.vendor_id}-${vendor.vendor_account || 0}-${
        vendor.location_id || 0
      }`,
      entity: vendor,
      pd_display_name: getNameToDisplay(vendor),
      pd_display_detail:
        vendor.address_line_1 ||
        vendor.city ||
        vendor.state ||
        vendor.postal_code
          ? `${vendor.address_line_1}, ${vendor.city}, ${vendor.state} ${vendor.postal_code}`
          : '(no address)',
      pd_address: null,
    }))

    if (!searchValue || 'none'.includes(searchValue.trim().toLowerCase())) {
      return [
        liveListNoneOption as LiveListEntity<EntrataVendor>,
        ...entrataOptions,
      ]
    }
    return entrataOptions
  }, [entrataVendors, isPending, searchValue])

  const fields = useProjectGridFieldsContext()
  const vendorNameField = fields.find((field) => field.name === 'Vendor Name')
  const vendorRemitToField = fields.find(
    (field) => field.name === 'Vendor Remit To',
  )

  const { value: vendorName } = useFieldValue({
    field: vendorNameField,
  })

  const { value: vendorRemitTo } = useFieldValue({
    field: vendorRemitToField,
  })

  const suggestedSearches = [
    ['vendorName', vendorName],
    ['vendorRemitTo', vendorRemitTo],
  ].filter(([, suggestion]) => suggestion) as [string, string][]

  if (vendorRemitTo === vendorName) {
    suggestedSearches.pop()
  }

  return (
    <BasePickerField<LiveListEntity<EntrataVendor>>
      value={jsonValue}
      label="Invoice Vendor"
      setDisableArrowKeys={() => false}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onChange={(newValue) => {
        setOpen(false)
        onManualChange(
          newValue?.entity_id === 'None'
            ? 'None'
            : getNameToDisplay(newValue?.entity),
          null,
          newValue,
        )
      }}
      openOnFocus={openOnFocus}
      options={options}
      open={open}
      setOpen={setOpen}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetching={isFetching}
      getOptionLabel={(option) =>
        option.entity_id === 'None' ? 'None' : getNameToDisplay(option.entity)
      }
      isLoading={needToTypeFirst ? false : isPending}
      isOptionEqualToValue={(option, value) =>
        option?.entity_id === value.entity_id
      }
      disabled={disabled || isDisabled || !clientOrgId}
      suggestedSearches={suggestedSearches}
      noOptionsText={
        needToTypeFirst ? 'Type to begin searching' : 'No vendors found'
      }
      renderOption={(props, option) => {
        return (
          <MenuItem
            {...props}
            key={option.entity_id}
            value={option.entity_id}
            sx={{ alignItems: 'center' }}
          >
            {option.pd_display_name}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, mt: '1px' }}
            >
              {option.pd_display_detail}
            </Typography>
          </MenuItem>
        )
      }}
      helperText={
        !disabled && isFetched && !clientOrgId
          ? 'Please set this document to a property before selecting a vendor.'
          : ''
      }
      InputLabelProps={{ shrink: true }}
    />
  )
}
